export interface ActivityFileStorage {
  readonly persistence: "session" | "browser" | "cloud";
  put(sourceId: string, file: File): Promise<void>;
  get(sourceId: string): Promise<File | null>;
  remove(sourceId: string): Promise<void>;
  clear(): Promise<void>;
}

function tracePersistence(message: string, details?: unknown): void {
  if (!import.meta.env.DEV) return;
  const timestamp = new Date().toISOString();
  if (details === undefined) {
    console.log(`[persistence ${timestamp}] ${message}`);
    return;
  }
  console.log(`[persistence ${timestamp}] ${message}`, details);
}

function tracePersistenceError(message: string, error: unknown): void {
  if (!import.meta.env.DEV) return;
  console.error(`[persistence ${new Date().toISOString()}] ${message}`, error);
}

export class BrowserSessionActivityFileStorage implements ActivityFileStorage {
  readonly persistence = "session" as const;
  private readonly files = new Map<string, File>();

  async put(sourceId: string, file: File): Promise<void> {
    this.files.set(sourceId, file);
  }

  async get(sourceId: string): Promise<File | null> {
    return this.files.get(sourceId) ?? null;
  }

  async remove(sourceId: string): Promise<void> {
    this.files.delete(sourceId);
  }

  async clear(): Promise<void> {
    this.files.clear();
  }
}

const DATABASE_NAME = "johan-performance-lab";
const STORE_NAME = "activity-files";

export class BrowserPersistentActivityFileStorage implements ActivityFileStorage {
  readonly persistence = "browser" as const;
  private readonly fallbackFiles = new Map<string, File>();
  private databasePromise: Promise<IDBDatabase | null> | null = null;

  private async getDatabase(): Promise<IDBDatabase | null> {
    if (this.databasePromise) {
      tracePersistence("activity file database returned existing Promise", { reason: "database initialization already started" });
      return this.databasePromise;
    }

    if (typeof indexedDB === "undefined") {
      tracePersistence("activity file database returned early", { reason: "IndexedDB is unavailable; using memory fallback" });
      this.databasePromise = Promise.resolve(null);
      return this.databasePromise;
    }

    this.databasePromise = new Promise((resolve) => {
      tracePersistence("open activity file database", { databaseName: DATABASE_NAME, version: 1 });
      const request = indexedDB.open(DATABASE_NAME, 1);
      request.onerror = () => {
        tracePersistenceError("activity file database open failed; resolving memory fallback", request.error ?? new Error("IndexedDB activity file database open failed."));
        resolve(null);
      };
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      };
      request.onsuccess = () => {
        tracePersistence("activity file database opened", { databaseName: DATABASE_NAME });
        resolve(request.result);
      };
    });

    return this.databasePromise;
  }

  private async runTransaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore, resolve: (value: T) => void, reject: (error: unknown) => void) => void, fallback: () => T): Promise<T> {
    tracePersistence("awaiting Promise: activity file database", { mode });
    const database = await this.getDatabase();
    tracePersistence("Promise resolved: activity file database", { mode, databaseAvailable: database !== null });
    if (!database) {
      tracePersistence("activity file transaction returned early", { reason: "database is unavailable; using memory fallback", mode });
      return fallback();
    }

    tracePersistence("awaiting Promise: activity file IndexedDB transaction", { mode, storeName: STORE_NAME });
    return new Promise<T>((resolve) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const store = transaction.objectStore(STORE_NAME);
      operation(store, value => {
        tracePersistence("Promise resolved: activity file IndexedDB request", { mode, storeName: STORE_NAME });
        resolve(value);
      }, error => {
        tracePersistenceError("activity file IndexedDB request failed; resolving memory fallback", error);
        resolve(fallback());
      });
      transaction.onerror = () => {
        tracePersistenceError("activity file IndexedDB transaction failed; resolving memory fallback", transaction.error ?? new Error("IndexedDB activity file transaction failed."));
        resolve(fallback());
      };
    });
  }

  async put(sourceId: string, file: File): Promise<void> {
    tracePersistence("activity file save begin", { sourceId, fileName: file.name, sizeBytes: file.size });
    this.fallbackFiles.set(sourceId, file);
    await this.runTransaction("readwrite", (store, resolve, reject) => {
      const request = store.put(file, sourceId);
      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB activity file put request failed."));
    }, () => undefined);
    tracePersistence("activity file save complete", { sourceId });
  }

  async get(sourceId: string): Promise<File | null> {
    tracePersistence("activity file restore begin", { sourceId });
    const file = await this.runTransaction("readonly", (store, resolve, reject) => {
      const request = store.get(sourceId);
      request.onsuccess = () => resolve((request.result as File | undefined) ?? this.fallbackFiles.get(sourceId) ?? null);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB activity file get request failed."));
    }, () => this.fallbackFiles.get(sourceId) ?? null);
    tracePersistence("activity file restore complete", { sourceId, filePresent: file !== null });
    return file;
  }

  async remove(sourceId: string): Promise<void> {
    this.fallbackFiles.delete(sourceId);
    await this.runTransaction("readwrite", (store, resolve, reject) => {
      const request = store.delete(sourceId);
      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB activity file delete request failed."));
    }, () => undefined);
  }

  async clear(): Promise<void> {
    this.fallbackFiles.clear();
    await this.runTransaction("readwrite", (store, resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve(undefined);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB activity file clear request failed."));
    }, () => undefined);
  }
}